
import { useState } from "react"
import { Accordion } from "react-bootstrap"
import PropTypes from "prop-types"

const QuestionnaireSection = ({ prescriptionId }) => {
  const [activeAccordion, setActiveAccordion] = useState(null)

  const followupListData = [
    {
      id: 1,
      created_at: "2024-01-15T10:30:00Z",
      questionnaire: [
        {
          isGroup: "No",
          question: "How are you feeling since your last visit?",
          answer: "Much Better",
          is_important: "Yes",
          answer_note: [
            {
              note_description: "Patient reports significant improvement in energy levels",
            },
          ],
        },
        {
          isGroup: "Yes",
          group_name: "Medication Compliance",
          questions: [
            {
              question: "Are you taking medications as prescribed?",
              answer: "Yes",
              is_important: "Yes",
              answer_note: [],
            },
            {
              question: "Any missed doses in the past week?",
              answer: "No",
              is_important: "No",
              answer_note: [],
            },
          ],
        },
        {
          isGroup: "No",
          question: "Rate your overall satisfaction (1-5)",
          answer: "5 - Very Satisfied",
          is_important: "No",
          answer_note: [],
        },
      ],
      notes: [
        {
          note_description: "Patient shows excellent progress. Continue current treatment plan.",
          note_type: "Clinical",
          is_important: "Yes",
          is_emergency: "No",
        },
        {
          note_description: "Schedule next follow-up in 2 weeks.",
          note_type: "Administrative",
          is_important: "No",
          is_emergency: "No",
        },
      ],
    },
    {
      id: 2,
      created_at: "2024-01-01T14:15:00Z",
      questionnaire: [
        {
          isGroup: "No",
          question: "Any side effects experienced?",
          answer: "Mild nausea in the morning",
          is_important: "Yes",
          answer_note: [
            {
              note_description: "Occurs only on empty stomach, resolved with food",
            },
          ],
        },
        {
          isGroup: "No",
          question: "Pain level (1-10)",
          answer: "3",
          is_important: "Yes",
          answer_note: [],
        },
      ],
      notes: [
        {
          note_description: "Adjust medication timing to reduce morning nausea.",
          note_type: "Clinical",
          is_important: "Yes",
          is_emergency: "No",
        },
      ],
    },
  ]

  // Function to format date and time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString)
    const datePart = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
    const timePart = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    return `${datePart} ${timePart}`
  }

  // Function to handle accordion selection
  const handleAccordionSelect = (eventKey) => {
    setActiveAccordion(eventKey)
  }

  return (
    <div className="mb-4">
      <h5 className="fw-bold mb-3">Past Follow-ups</h5>
      {followupListData.length === 0 ? (
        <div className="alert alert-info">No follow-up data available.</div>
      ) : (
        <Accordion activeKey={activeAccordion} onSelect={handleAccordionSelect} className="accordion accordion-primary">
          {followupListData.map((followup, index) => (
            <Accordion.Item key={index} eventKey={index.toString()}>
              <Accordion.Header>
                <div className="d-flex justify-content-between align-items-center w-100">
                  <div>
                    <strong>Date: {formatDateTime(followup.created_at)}</strong>
                  </div>
                  <div className="me-4">
                    <span className="badge bg-warning me-2">{followup.questionnaire?.length || 0} Questions</span>
                    <span className="badge bg-info">{followup.notes?.length || 0} Notes</span>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                {/* Questionnaire Section */}
                {followup.questionnaire && followup.questionnaire.length > 0 && (
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3">Questionnaire</h6>
                    {followup.questionnaire.map((item, qIndex) => {
                      return item.isGroup === "No" ? (
                        <div key={qIndex} className="mb-3 p-3 border rounded">
                          <div className="d-flex justify-content-between">
                            <strong className="text-primary">{item.question}</strong>
                            {item.is_important === "Yes" && <span className="badge bg-warning">Important</span>}
                          </div>
                          <p className="mb-0">{item.answer}</p>
                          {item.answer_note.length > 0 && (
                            <div className="p-2 text-muted bg-light rounded">
                              <small>Note: {item.answer_note[0]?.note_description}</small>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div key={qIndex} className="mb-3 p-3 border rounded">
                          <div className="justify-content-between">
                            <strong className="text-primary">{item.group_name}</strong>
                            {item.questions.map((question, qaIndex) => (
                              <div key={qaIndex} className="mb-2 mt-4">
                                <strong>{question.question}</strong>
                                {question.is_important === "Yes" && <span className="badge bg-warning">Important</span>}
                                <p className="mb-0">{question.answer}</p>
                                {question.answer_note.length > 0 && (
                                  <div className="p-2 text-muted bg-light rounded">
                                    <small>Note: {question.answer_note[0]?.note_description}</small>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Notes Section */}
                {followup.notes && followup.notes.length > 0 && (
                  <div>
                    <h6 className="fw-bold mb-3">Notes</h6>
                    {followup.notes.map((note, nIndex) => (
                      <div key={nIndex} className="mb-3 p-3 border rounded bg-light">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <p className="mb-2">{note.note_description}</p>
                            <small className="text-muted">Type: {note.note_type}</small>
                          </div>
                          <div className="ms-3">
                            {note.is_important === "Yes" && <span className="badge bg-warning me-1">Important</span>}
                            {note.is_emergency === "Yes" && <span className="badge bg-danger">Emergency</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </div>
  )
}

QuestionnaireSection.propTypes = {
  prescriptionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
}

export default QuestionnaireSection;
